/**
 * Safely parses a value that may arrive as a JSON string from the database.
 *
 * JSONB columns can arrive as serialised strings depending on column type,
 * views, RPCs, or client version.  This helper normalises the value so
 * every consumer gets a parsed object (or null on failure).
 */
export function safeParseJson<T>(value: T | string | null | undefined): T | null {
  if (value == null) return null;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      // JSON.parse("null") returns null — treat it the same as missing
      if (parsed == null) return null;
      return parsed as T;
    } catch {
      return null;
    }
  }
  return value as T;
}

/**
 * A jsonb object column read tolerantly: double-encoded strings are unwrapped, and anything
 * that is not a plain object comes back as {} so it can be spread and merged safely.
 */
export function safeParseJsonObject(value: unknown): Record<string, unknown> {
  let parsed = safeParseJson<unknown>(value);
  if (typeof parsed === 'string') parsed = safeParseJson<unknown>(parsed);
  return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : {};
}
